/**
 * Porta de entrada do aplicativo (F1.8).
 *
 * Só três estados existem aqui, e a ordem importa:
 *   1. ainda não sei quem é você  → tela de carregando (nunca um flash da tela de entrar);
 *   2. não entrou                 → tela de entrar;
 *   3. entrou                     → painel.
 *
 * Por que não usar biblioteca de rotas: o painel da F1 tem duas telas e o app de campo (F3) vai
 * ser guiado por tarefa, não por URL. Enquanto isso for verdade, uma dependência a menos é uma
 * atualização a menos para quebrar o aplicativo instalado no celular do técnico.
 */
import { useCallback, useEffect, useState } from 'react';
import { Entrar } from './telas/Entrar';
import { Layout } from './telas/Layout';
import { Equipe } from './telas/Equipe';
import { Configuracoes } from './telas/Configuracoes';
import { EstadoDaTela } from './componentes/EstadoDaTela';
import { carregarSessao, type UsuarioSessao } from './auth/sessao';
import { supabase } from './lib/supabase';

type Situacao = 'verificando' | 'fora' | 'dentro';

export function App() {
  const [situacao, setSituacao] = useState<Situacao>('verificando');
  const [sessao, setSessao] = useState<UsuarioSessao | null>(null);
  const [erroConfiguracao, setErroConfiguracao] = useState<string | null>(null);
  const [empresaNome, setEmpresaNome] = useState<string | undefined>(undefined);

  const buscar = useCallback(async () => {
    try {
      const s = await carregarSessao();
      setSessao(s);
      setSituacao(s ? 'dentro' : 'fora');
      if (s) {
        // O nome da empresa aparece na barra de cima o tempo todo: sem ele, o canto fica
        // preso em "Carregando…" e dá a impressão de que a tela travou.
        const { data } = await supabase().from('empresa').select('nome').eq('id', s.empresa_id).maybeSingle();
        setEmpresaNome((data as { nome?: string } | null)?.nome ?? undefined);
      }
    } catch (erro) {
      // Configuração ausente (sem URL/chave do servidor) não é culpa de quem está usando:
      // mostra a frase e para, em vez de piscar a tela de entrar para sempre.
      setErroConfiguracao(erro instanceof Error ? erro.message : String(erro));
      setSituacao('fora');
    }
  }, []);

  useEffect(() => {
    // Fora do corpo síncrono do efeito: `buscar` muda estado e chamá-la direto faria a
    // renderização em cascata que o lint (com razão) proíbe.
    queueMicrotask(() => void buscar());
    let inscricao: { unsubscribe: () => void } | null = null;
    try {
      const { data } = supabase().auth.onAuthStateChange((evento) => {
        // Sair em outra aba, expirar o refresh ou entrar em outro lugar tem de refletir aqui.
        // O `queueMicrotask` evita mudar estado no meio do próprio efeito (renderização em cascata).
        queueMicrotask(() => {
          if (evento === 'SIGNED_OUT') {
            setSessao(null);
            setSituacao('fora');
          } else if (evento === 'SIGNED_IN' || evento === 'TOKEN_REFRESHED') {
            void buscar();
          }
        });
      });
      inscricao = data.subscription;
    } catch {
      /* sem configuração: o erro já foi tratado em buscar() */
    }
    return () => inscricao?.unsubscribe();
  }, [buscar]);

  if (erroConfiguracao) {
    return (
      <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 'var(--esp-6)' }}>
        <EstadoDaTela
          estado="falha_recuperavel"
          especifico={{
            texto:
              'Este aplicativo ainda não foi configurado para conversar com o servidor. Avise o responsável técnico.',
            acao: null,
            porque:
              'Falta de configuração não é falha de rede: mandar "tentar de novo" faria a pessoa repetir para sempre.',
          }}
        />
      </main>
    );
  }

  if (situacao === 'verificando') {
    return (
      <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 'var(--esp-6)' }}>
        <EstadoDaTela estado="carregando" />
      </main>
    );
  }

  if (situacao === 'fora' || !sessao) {
    return <Entrar onEntrou={() => void buscar()} />;
  }

  return (
    <Layout
      sessao={sessao}
      empresaNome={empresaNome}
      aoSair={() => {
        setSessao(null);
        setSituacao('fora');
      }}
    >
      {(secao) => (secao === 'configuracoes' ? <Configuracoes sessao={sessao} /> : <Equipe sessao={sessao} />)}
    </Layout>
  );
}
